import type { SessionRecordingExplainerVariant, SetupAudience, SetupStepId } from '@shared/setup'

/**
 * Easy Read copy for setup (design §3.3–3.5): short sentences, one idea each,
 * everyday words. The call-recording wording lives in
 * `@shared/calls/recording-consent` (`RECORDING_POLICY_EASY_READ_COPY`) so the
 * stored wording id always matches what was shown.
 */

export type CopyContext = {
  audience: SetupAudience
  platform: 'ios' | 'android' | 'web'
  /** Independent who follows someone's sessions. */
  sessionFollower: boolean
  consentGiver: boolean
  premiumSessionRecording: boolean
  /** "Face ID" / "fingerprint" … */
  biometricLabel: string
  explainerVariant: SessionRecordingExplainerVariant | null
  /** First names this Independent may give consent for. */
  consentGiverNames: string[]
}

export type StepCopy = {
  title: string
  lines: string[]
  /** "If you say no: …" */
  ifNo?: string
  /** A platform tip shown under the lines. */
  tip?: string
  primary: string
  /** Absent for information steps (a single "Got it"). */
  later?: string
  /**
   * Numbered steps for a step that is only changed in Settings (lock-screen
   * calls, calls channel, precise location), shown with the Open settings button.
   */
  settingsSteps?: string[]
}

export const NOT_NOW = 'Not now'

function settingsWord(platform: CopyContext['platform']): string {
  return platform === 'web' ? 'browser' : 'phone'
}

/** "It's turned off in your phone settings." */
export function blockedLine(platform: CopyContext['platform']): string {
  return `It’s turned off in your ${settingsWord(platform)} settings.`
}

/** Settings steps for an OS-blocked notifications permission (§3.3). */
export function notificationSettingsSteps(platform: CopyContext['platform']): string[] {
  if (platform === 'ios') {
    return ['Tap Open Settings', 'Tap Notifications', 'Turn on Allow Notifications', 'Come back here']
  }
  return ['Tap Open Settings', 'Tap Notifications', 'Turn on All notifications', 'Come back here']
}

function consentNames(names: string[]): string {
  if (names.length === 1) return names[0]
  return 'the people you support'
}

export function stepCopy(id: SetupStepId, ctx: CopyContext): StepCopy {
  switch (id) {
    case 'notifications': {
      // Android rings for calls with alerts off (see push-registration); iOS and web don't promise that.
      const android = ctx.platform === 'android'
      const lines = [android ? 'We tell you about new messages and missed calls.' : 'We tell you about new messages and calls.']
      if (ctx.audience === 'member' || ctx.sessionFollower) {
        lines.push('We tell you when your support worker is on the way.')
      }
      if (ctx.audience === 'provider') lines.push('We remind you about your sessions.')
      if (ctx.consentGiver) lines.push('We tell you when someone asks you to agree to a recording.')
      return {
        title: 'Get alerts',
        lines,
        ifNo: android
          ? 'You will only see messages when the app is open. Calls will still ring.'
          : 'You will only see messages when the app is open. You might miss calls.',
        primary: 'Turn on alerts',
        later: NOT_NOW,
      }
    }
    case 'calls-channel':
      return {
        title: 'Let calls ring',
        lines: ['Call alerts are turned off for Ascend.', 'If they stay off, calls will not ring.'],
        primary: 'Open settings',
        later: NOT_NOW,
        settingsSteps: ['Turn on “Incoming calls”', 'If you see “Pop on screen”, turn it on', 'Come back here'],
      }
    case 'lock-screen-calls':
      return {
        title: 'Calls on your lock screen',
        lines: ['This lets a call fill your screen when your phone is locked.'],
        ifNo: 'Calls show as a small alert. You might miss them.',
        primary: 'Open settings',
        later: NOT_NOW,
        settingsSteps: ['Turn on “Allow full screen notifications”', 'Come back here'],
      }
    case 'battery':
      return {
        title: 'Keep calls working',
        lines: [
          'Your phone can put Ascend to sleep to save battery.',
          'Then calls may not ring.',
          'Tap Allow on the next screen.',
        ],
        ifNo: 'Calls might not ring when your phone has been still for a while.',
        primary: 'Allow',
        later: NOT_NOW,
      }
    case 'microphone': {
      const lines = ['Ascend uses your microphone for calls and voice messages.']
      if (ctx.premiumSessionRecording) lines.push('It also records sessions when you choose.')
      return {
        title: 'Talk on calls',
        lines,
        ifNo: 'People will not hear you on calls.',
        primary: 'Allow microphone',
        later: NOT_NOW,
      }
    }
    case 'camera':
      return {
        title: 'Video and photos',
        lines: ['Ascend uses your camera for video calls.', 'You can also take photos to send.'],
        ifNo:
          ctx.audience === 'provider'
            ? 'Video sessions and video calls will not work.'
            : 'You can still use voice calls. Video will be off.',
        primary: 'Allow camera',
        later: NOT_NOW,
      }
    case 'location':
      return {
        title: 'Your location for sessions',
        lines: [
          'Ascend uses your location only during a session you start.',
          'It records your travel kilometres.',
          'It tells your client when you are close.',
          'It stops when you check out.',
        ],
        tip:
          ctx.platform === 'ios'
            ? 'Choose Allow While Using App. Keep Precise on.'
            : 'Choose While using the app. Choose Precise.',
        ifNo: 'Your travel and arrival will not be recorded.',
        primary: 'Allow location',
        later: NOT_NOW,
      }
    case 'location-precise':
      return {
        title: 'Turn on precise location',
        lines: [
          'Your phone is sharing an approximate location.',
          'It can’t measure kilometres or know when you arrive.',
        ],
        primary: 'Open settings',
        later: NOT_NOW,
        settingsSteps:
          ctx.platform === 'ios'
            ? ['Tap Location', 'Turn on Precise Location', 'Come back here']
            : ['Tap Location', 'Turn on Use precise location', 'Come back here'],
      }
    case 'session-recording-info':
      return explainerCopy(ctx)
    case 'app-lock':
      return {
        title: 'Lock the app',
        lines: [
          `Use ${ctx.biometricLabel} to open Ascend.`,
          'This keeps your messages private if someone else uses your phone.',
        ],
        ifNo: 'Nothing changes. You can turn this on later in Menu.',
        primary: `Turn on ${ctx.biometricLabel}`,
        later: NOT_NOW,
      }
    case 'recording-consent':
      // Rendered by RecordingConsentContent from the shared Easy Read copy.
      return { title: 'Calls are recorded', lines: [], primary: '' }
  }
}

function explainerCopy(ctx: CopyContext): StepCopy {
  if (ctx.explainerVariant === 'consent-giver') {
    const one = ctx.consentGiverNames.length === 1
    const who = consentNames(ctx.consentGiverNames)
    return {
      title: one ? `Saying yes for ${who}` : 'Saying yes for the people you support',
      lines: [
        one
          ? `${who}’s therapist may ask to record a session.`
          : 'Their therapist may ask to record a session.',
        'You may get a request in Ascend.',
        'Open it and tap Yes or No.',
        'For someone under 18, only a parent or guardian can say yes.',
      ],
      primary: 'Got it',
    }
  }
  if (ctx.explainerVariant === 'psr') {
    return {
      title: 'Recording whole sessions',
      lines: [
        'Always ask before you record.',
        'They can tap Yes on the card, say yes out loud, or show it without words.',
        'Or someone marked ‘Can give consent’ can agree in their app.',
        'Check the AI note before you approve it.',
      ],
      primary: 'Got it',
    }
  }
  return {
    title: 'Recording a session',
    lines: [
      'Sometimes your therapist may ask to record a session.',
      'They ask you first, every time.',
      'You can say yes or no.',
      'You can say stop at any time.',
      'The sound is deleted after the notes are checked.',
    ],
    primary: 'Got it',
  }
}

export const OVERVIEW_COPY = {
  new: {
    title: 'Let’s set up Ascend',
    lines: [
      'This takes about 2 minutes.',
      'We will ask to use some things on your phone.',
      'We tell you why first.',
    ],
    primary: 'Start',
  },
  someDone: {
    title: 'Check your set up',
    lines: ['Some things are already done ✓'],
    primary: 'Continue',
  },
  allDone: {
    title: 'You’re all set ✓',
    lines: ['Everything is turned on.'],
    primary: 'Done',
  },
  adminReset: 'Your organisation asked you to check your set up.',
  devicePending: 'This phone is waiting for approval. Approve it on your other device. Calls and messages still work.',
  iosSafari: 'To get alerts on iPhone, use the Ascend Messenger app.',
} as const

export const DONE_COPY = {
  allDone: { title: 'You’re all set ✓', primary: 'Continue' },
  tourNext: 'Next, a quick tour of the app.',
  tourLater: 'You can see a tour any time in Menu.',
  almost: { title: 'Almost done', primary: 'Continue' },
  remindNext: 'We will remind you next time you open Ascend.',
} as const

export function thingsStillToDo(n: number): string {
  return n === 1 ? '1 thing still needs doing:' : `${n} things still need doing:`
}

export const REMINDER_COPY = {
  title: 'Finish setting up',
  primary: 'Finish now',
  later: 'Later',
} as const

export function reminderLine(n: number): string {
  return n === 1
    ? '1 thing still needs doing so calls and alerts work.'
    : `${n} things still need doing so calls and alerts work.`
}

/** Slim banner after a blocked required step was put off 3 times (§3.6). */
/** Steps that stop calls working when they're left undone. */
const CALL_STEPS: ReadonlySet<SetupStepId> = new Set([
  'calls-channel',
  'lock-screen-calls',
  'battery',
  'microphone',
  'recording-consent',
])

export function reminderBannerText(steps: readonly SetupStepId[], platform: CopyContext['platform']): string {
  const alertsRing = platform !== 'android'
  if (steps.length === 1) {
    if (steps[0] === 'notifications') {
      return alertsRing ? 'Alerts are off — calls may be missed' : 'Alerts are off — you may miss messages'
    }
    if (steps[0] === 'microphone') return 'Microphone is off — people can’t hear you'
    if (steps[0] === 'recording-consent') return 'Calls are recorded — please read this'
  }
  const affectsCalls = steps.some((id) => CALL_STEPS.has(id) || (id === 'notifications' && alertsRing))
  return affectsCalls ? 'Some settings are off — calls may not work' : 'Some settings are off — tap Fix'
}

/** Short Easy Read titles for checklists (overview, reminder, done). */
export const STEP_LIST_TITLES: Record<SetupStepId, string> = {
  notifications: 'Get alerts',
  'calls-channel': 'Let calls ring',
  'lock-screen-calls': 'Calls on your lock screen',
  battery: 'Keep calls working',
  microphone: 'Talk on calls',
  camera: 'Video and photos',
  location: 'Your location for sessions',
  'location-precise': 'Precise location',
  'recording-consent': 'Calls are recorded',
  'session-recording-info': 'Recording a session',
  'app-lock': 'Lock the app',
}

export const TURNED_ON = 'Turned on ✓'

/** Said after an "Allow" tap that did not turn the thing on (Easy Read). */
export const PHONE_WONT_ASK_NOTICE = 'Your phone will not ask again. Open Settings to turn it on.'
export const PHONE_SILENT_NOTICE = 'Your phone is not showing its question. Open Settings to turn it on.'
export const TRY_AGAIN_NOTICE = 'It is not on yet. Tap the button to try again.'
export const STILL_OFF_NOTICE = 'It is still off. Open Settings, then come back here.'
